import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import { useQueryClient } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

import Router from '../../utils/router';
import { Button } from '../../../../main/scripts/lumen';
import Spinner from '../../../../main/scripts/components/core/Spinner';
import {
  AppShell,
  CopyProjectModal,
  DeleteProjectModal,
  QUERY_KEYS,
  RenameProjectModal,
  usePersistedState,
  useWorkspace
} from '../../../../main/scripts/shell';
import { thousandify } from '../../../../main/scripts/utils/NumFmtUtils';
import naturalSort from '../../../../main/scripts/utils/NaturalSort';
import { Version } from '../../../../main/scripts/utils/version';
import { getScrapersV2Status } from '../../../../main/scripts/utils/ApiUtilsV5';
import { RebuildProjectModal } from './RebuildProjectModal';
import { getBuildInfoProperties } from './getBuildInfoProperties';
import { ProjectCard } from './ProjectCard';
import { ProjectsGrid } from './ProjectsGrid';
import { ProjectsList } from './ProjectsList';
import { ProjectsToolbar } from './ProjectsToolbar';
import { statusToPill } from './StatusPill';

const SORT_FNS = {
  'name-asc': (a, b) => naturalSort(a.name, b.name),
  'name-desc': (a, b) => -naturalSort(a.name, b.name),
  'date-desc': (a, b) => b.creation_date - a.creation_date,
  'date-asc': (a, b) => a.creation_date - b.creation_date
};

const PAGE_SIZE = 60;
const SCROLL_THRESHOLD_PX = 400;

// A failed build never replaces the previous one — it is assembled elsewhere
// and only swapped in on success — so a project that has built successfully
// before is still explorable. Locking it would hide data and dashboards that
// are perfectly intact, which is what a failed upload used to do.
const isExplorable = project => {
  const info = project.last_build_info || {};
  if (typeof info.start_time !== 'number') return false;
  return info.success !== false || project.last_successful_build_time != null;
};

const isScienceOutdated = (project, apiStatus) => {
  const minVersion = apiStatus?.minimum_science_version;
  const projectVersion = project?.last_build_info?.science_version;
  if (!minVersion || !projectVersion) return false;
  return new Version(projectVersion).isLessThan(minVersion);
};

export const ProjectsPage = () => (
  <AppShell activeTab="projects">
    <ProjectsView />
  </AppShell>
);

const ProjectsView = () => {
  const {
    workspaceId,
    isAllWorkspaces,
    workspaces,
    projects,
    projectsLoading: loading,
    profile,
    apiStatus
  } = useWorkspace();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = usePersistedState('sortProjectsBy', 'name-asc');
  const [viewMode, setViewMode] = usePersistedState('projectsViewMode', 'grid');
  const [copyingProject, setCopyingProject] = useState(null);
  const [renamingProject, setRenamingProject] = useState(null);
  const [rebuildingProject, setRebuildingProject] = useState(null);
  const [deletingProject, setDeletingProject] = useState(null);
  const [scrapesByProject, setScrapesByProject] = useState({});

  const hasWritableWorkspace = workspaces.some(w =>
    w.permissions?.includes('write')
  );

  useEffect(() => {
    document.title = 'Projects | Cemantica Insights hub';
  }, []);

  useEffect(() => {
    let active = true;
    // const { new_scrapers: newScrapers } = getFeatureFlags() || {};
    // console.log(newScrapers);
    // if (!newScrapers) return undefined;

    Promise.resolve(getScrapersV2Status())
      .catch(() => [])
      .then(v2Scrapes => {
        if (!active) return;
        const byProject = {};
        (Array.isArray(v2Scrapes) ? v2Scrapes : []).forEach(s => {
          if (!s.project_id) return;
          if (!byProject[s.project_id]) byProject[s.project_id] = [];
          byProject[s.project_id].push(s);
        });

        // Most recent scrape first, matching the legacy behaviour.
        Object.values(byProject).forEach(list =>
          list.sort((a, b) => new Date(b.start_time) - new Date(a.start_time))
        );

        setScrapesByProject(byProject);
      });

    return () => {
      active = false;
    };
  }, []);

  const visibleProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = projects.filter(project => {
      const searchable = `${project.name}\n${project.description || ''}`.toLowerCase();
      const matchesSearch = !q || searchable.includes(q);
      const matchesWorkspace =
        !workspaceId ||
        isAllWorkspaces ||
        project.workspace_id === workspaceId;
      return matchesSearch && matchesWorkspace;
    });
    return [...filtered].sort(SORT_FNS[sortBy] || SORT_FNS['name-asc']);
  }, [projects, search, workspaceId, isAllWorkspaces, sortBy]);

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, workspaceId, sortBy]);

  const shownProjects = visibleProjects.slice(0, visibleCount);
  const hasMore = visibleCount < visibleProjects.length;

  const showMore = () =>
    setVisibleCount(count => Math.min(count + PAGE_SIZE, visibleProjects.length));

  const handleScroll = event => {
    if (!hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight <= SCROLL_THRESHOLD_PX) {
      showMore();
    }
  };

  const handleAddData = project => {
    Router.go(
      `app/upload/stream-edit/${project.workspace_id}/${project.project_id}`
    );
  };

  const handleNewProject = () => Router.go('app/upload/stream');

  const cards = shownProjects.map(project => {
    const outdated = isScienceOutdated(project, apiStatus);
    const projectWithScrapes = {
      ...project,
      scrapes: scrapesByProject[project.project_id] || []
    };
    const { status } = getBuildInfoProperties(projectWithScrapes, outdated);
    const pill = statusToPill(status);
    const description =
      project.description?.trim().length > 0 ? project.description : undefined;
    const explorable = isExplorable(project);
    const href =
      explorable && !outdated
        ? `/app/projects/${project.workspace_id}/${project.project_id}`
        : undefined;
    const onClickCard = outdated
      ? event => {
          event.preventDefault();
          setRebuildingProject(project);
        }
      : undefined;

    const canWrite = project.permissions.includes('write');
    const canCreate = project.permissions.includes('create');

    return (
      <ProjectCard
        key={project.project_id}
        variant={viewMode}
        status={pill}
        name={project.name}
        description={description}
        recordCount={`${thousandify(project.document_count || 0)} Records`}
        createdAt={`Created on ${moment(project.creation_date * 1000).format('MMM D, YYYY')}`}
        statusMessage={status.message || undefined}
        warning={status.warning || undefined}
        href={href}
        onClickCard={onClickCard}
        onAddData={canWrite ? () => handleAddData(project) : undefined}
        onCopy={hasWritableWorkspace ? () => setCopyingProject(project) : undefined}
        onRename={canWrite ? () => setRenamingProject(project) : undefined}
        onDelete={canCreate ? () => setDeletingProject(project) : undefined}
      />
    );
  });

  return (
    <>
      <TitleRow>
        <PageTitle>Projects</PageTitle>
        <Button
          variant="primary"
          size="lg"
          onClick={handleNewProject}
          aria-label="Create new project"
        >
          <FontAwesomeIcon
            icon={faPlus}
            style={{ width: 16, height: 16, marginRight: 8 }}
          />
          New Project
        </Button>
      </TitleRow>
      <ProjectsToolbar
        searchValue={search}
        onSearchChange={setSearch}
        sortValue={sortBy}
        onSortChange={setSortBy}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />
      <ScrollArea onScroll={handleScroll} data-test-id="projects-scroll-area">
        {loading ? (
          <SpinnerWrap>
            <Spinner size="large" />
          </SpinnerWrap>
        ) : visibleProjects.length === 0 ? (
          <EmptyState>
            {search
              ? 'No projects match your search.'
              : "You don't have any projects yet."}
          </EmptyState>
        ) : (
          <>
            {viewMode === 'grid' ? (
              <ProjectsGrid>{cards}</ProjectsGrid>
            ) : (
              <ProjectsList>{cards}</ProjectsList>
            )}
            {hasMore && (
              <MoreRow>
                <MoreCount>
                  Showing {thousandify(shownProjects.length)} of{' '}
                  {thousandify(visibleProjects.length)} projects
                </MoreCount>
                <Button variant="secondary" onClick={showMore}>
                  Show more
                </Button>
              </MoreRow>
            )}
          </>
        )}
      </ScrollArea>
      {copyingProject && (
        <CopyProjectModal
          project={copyingProject}
          workspaces={workspaces}
          onClose={() => setCopyingProject(null)}
          onCopied={() => queryClient.invalidateQueries(QUERY_KEYS.PROJECTS)}
        />
      )}
      {renamingProject && (
        <RenameProjectModal
          project={renamingProject}
          onClose={() => setRenamingProject(null)}
          onSaved={() => queryClient.invalidateQueries(QUERY_KEYS.PROJECTS)}
        />
      )}
      {rebuildingProject && (
        <RebuildProjectModal
          project={rebuildingProject}
          username={profile?.username}
          serverStatus={apiStatus}
          onClose={() => setRebuildingProject(null)}
        />
      )}
      {deletingProject && (
        <DeleteProjectModal
          project={deletingProject}
          onClose={() => setDeletingProject(null)}
          onDeleted={() => queryClient.invalidateQueries(QUERY_KEYS.PROJECTS)}
        />
      )}
    </>
  );
};


const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin: -4px -32px -30px;
  padding: 4px 32px 30px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 40px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.fg.default};
`;

const SpinnerWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
`;

const MoreRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 0 8px;
`;

const MoreCount = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.color.fg.muted};
`;

const EmptyState = styled.div`
  padding: 48px;
  text-align: center;
  color: ${({ theme }) => theme.color.fg.muted};
  font-size: 16px;
  background: ${({ theme }) => theme.color.bg.default};
  border: 1px dashed ${({ theme }) => theme.color.slate[200]};
  border-radius: 12px;
`;
